import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '@/pages/configuration/routeSlice';
import { ActionRegistry } from '@packages/table-builder';

/**
 * Builds the ABP policy key from the DynamicTableV2 schema permission format.
 *
 * Schema format:  `{ action: 'Edit', resource: 'Participant' }`
 * ABP format:     `CustomerService.Customer.Participant.Edit`
 *                 `CustomerService.Customer.Participant` (when action is 'View')
 */
function buildPolicyKey(action: string, resource: string): string {
  let key = `CustomerService.Customer.${resource}`;
  if (action !== 'View') {
    key += `.${action}`;
  }
  return key;
}

/**
 * Registers a global permission resolver on the ActionRegistry.
 *
 * Call once in `AuthenticatedContent` (or any layout-level component that
 * renders after auth). All `DynamicTableV2` instances will auto-resolve
 * `permission: { action, resource }` from the schema without needing a
 * `checkPermission` prop.
 *
 * The resolver reads `grantedPolicies` from the Redux auth slice on every
 * invocation — so it always reflects the current session's permissions,
 * including after a permission change event.
 */
export function useTablePermissionResolver(): void {
  const userAuthList = useSelector(selectAuthRoutesList);
  const policies = userAuthList?.auth?.grantedPolicies;

  useEffect(() => {
    ActionRegistry.setPermissionResolver((perm) => {
      if (!policies) return false;
      const key = buildPolicyKey(perm.action, perm.resource);
      return Boolean(policies[key]);
    });
  }, [policies]);
}
